import { useSvgBbox, useSvgTextSize } from '@festina-life/ui/hooks'
import { Group } from '@visx/group'
import { hierarchy, Tree } from '@visx/hierarchy'
import { useMemo } from 'react'

import type { ModelMetaType } from '../../types/metaModel'
import {
  CompositionStepWithConnectors,
  RHOMBUS_LONG_DIAGONAL_LENGTH,
} from './CompositionStepWithConnectors'
import {
  HORIZONTAL_NODE_INDENTATION,
  NODE_SPACING_HORIZONTAL,
  TEXT_BOX_PADDING_X,
  TEXT_BOX_PADDING_Y,
} from './constants'
import { flattenD3TreeNode, metaTypeToTreeNode } from './d3Helpers'
import { ModelTreeNode } from './ModelTreeNode'

type Props = {
  /** Root type for which the meta model is being shown. */
  rootType: ModelMetaType
}

/** Component used for rendering an element inside the Festina Meta Model. Other than showing the graphical representation
 * the diagram also shows which is the currently active path, highlighting the elements that currently build up the main
 * view.
 */
export const ModelDiagram = ({ rootType }: Props) => {
  const { ref, width: bboxWidth, height: bboxHeight } = useSvgBbox()
  const { dimensions: currentSvgTextDimensions, svgRef } =
    useSvgTextSize<SVGGElement>({})

  const typeTree = useMemo(
    () => metaTypeToTreeNode({ metaType: rootType }),
    [rootType],
  )

  const d3Hierarchy = useMemo(
    () => hierarchy(typeTree, (d) => d.children?.map((child) => child.node)),
    [typeTree],
  )

  const nodeHeightWithoutSpacing =
    currentSvgTextDimensions.height + 2 * TEXT_BOX_PADDING_Y
  const nodeHeightWithPadding =
    nodeHeightWithoutSpacing + NODE_SPACING_HORIZONTAL

  return (
    <div>
      <svg width={bboxWidth} height={bboxHeight} ref={ref}>
        <Group innerRef={svgRef}>
          <Tree root={d3Hierarchy} size={[bboxWidth, bboxHeight]}>
            {(tree) => {
              flattenD3TreeNode({
                node: tree,
                nodeHeightWithPadding,
                indentation: HORIZONTAL_NODE_INDENTATION,
              })

              return (
                <Group>
                  {tree.links().map((link, index) => {
                    const linkTargetWithAssociationType =
                      link.source.data.children!.find(
                        (childNode) => childNode.node === link.target.data,
                      )!

                    return (
                      <CompositionStepWithConnectors
                        // oxlint-disable-next-line react/no-array-index-key
                        key={`${link.source.data.name ?? ''}-${link.target.data.name}-${index}`}
                        link={link}
                        cardinality={linkTargetWithAssociationType.cardinality}
                        association={linkTargetWithAssociationType.association}
                        sourceConnectorOffset={{
                          dy: nodeHeightWithoutSpacing,
                          dx:
                            TEXT_BOX_PADDING_X +
                            (linkTargetWithAssociationType.association ===
                            'subtype'
                              ? 0
                              : RHOMBUS_LONG_DIAGONAL_LENGTH),
                        }}
                        targetConnectorOffset={{
                          dy: nodeHeightWithoutSpacing / 2,
                          dx: 0,
                        }}
                      />
                    )
                  })}
                  {tree.descendants().map((node, index) => (
                    <ModelTreeNode
                      // oxlint-disable-next-line react/no-array-index-key
                      key={`${node.data.name}-${node.parent?.data?.name}-${index}`}
                      node={node}
                    />
                  ))}
                </Group>
              )
            }}
          </Tree>
        </Group>
      </svg>
    </div>
  )
}
